import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGetRatingResponseFullObject } from '../hooks/reactQuerys/RatingFormQueries';
import {
  answerDisplay,
  answerNoteText,
  ratingStatusColor,
  ratingStatusLabel,
} from '../services/ratingAnswerDisplay.service';
import { formatBackendDateTime } from '../services/backendTimestamp.service';

interface IProps {
  idRatingResponse: number | null;
  onClose: () => void;
}

/** Die Gesamtbewertung liegt immer auf der Skala 1-5 (`OVERALL_SCALE_MIN` /
 *  `OVERALL_SCALE_MAX` in ratingform.py). */
const OVERALL_SCALE_MAX = 5;

/** Sterne für einen Wert, der auf die Skala passt. Ob er das tut, entscheidet
 *  `answerDisplay` — hier wird nur noch gemalt. */
const StarsView: React.FC<{ value: number; total: number }> = ({
  value,
  total,
}) => (
  <Stack direction='row' spacing={0.25}>
    {Array.from({ length: total }, (_, i) => i + 1).map(n =>
      n <= value ? (
        <StarIcon key={n} fontSize='small' color='warning' />
      ) : (
        <StarOutlineIcon key={n} fontSize='small' color='disabled' />
      )
    )}
    <Typography variant='caption' sx={{ ml: 1, alignSelf: 'center' }}>
      {value}/{total}
    </Typography>
  </Stack>
);

/** Eine Antwort — Sterne, Zahl, Ja/Nein oder Freitext. Passt der Wert nicht
 *  zur heutigen Skala der Frage, erscheint statt einer erfundenen
 *  Sternedarstellung die Zahl mit Erläuterung (QS #9, TF13). */
const AnswerView: React.FC<{ answer: Parameters<typeof answerDisplay>[0] }> = ({
  answer,
}) => {
  const display = answerDisplay(answer);
  const note = answerNoteText(display.note, display.value, display.total);

  const body = (() => {
    switch (display.kind) {
      case 'stars':
        return (
          <StarsView value={display.value ?? 0} total={display.total ?? 0} />
        );
      case 'number':
        return (
          <Typography variant='body2'>
            {display.value}
            {display.total !== null ? `/${display.total}` : ''}
          </Typography>
        );
      case 'yesno':
        return (
          <Chip
            size='small'
            color={display.value === 1 ? 'success' : 'default'}
            label={display.value === 1 ? 'Ja' : 'Nein'}
          />
        );
      case 'text':
        return (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
            {display.text}
          </Typography>
        );
      default:
        return (
          <Typography variant='body2' color='text.secondary'>
            —
          </Typography>
        );
    }
  })();

  if (!note) return body;
  return (
    <Stack direction='row' spacing={0.5} alignItems='center'>
      {body}
      <Tooltip title={note}>
        <InfoOutlinedIcon fontSize='small' color='warning' />
      </Tooltip>
    </Stack>
  );
};

export const RatingResponseDetailDialog: React.FC<IProps> = ({
  idRatingResponse,
  onClose,
}) => {
  const open = idRatingResponse != null;
  const { response, isLoadingResponse } =
    useGetRatingResponseFullObject(idRatingResponse);

  const recipient = response
    ? [response.FirstName, response.LastName].filter(Boolean).join(' ') ||
      response.Email ||
      `Mandant #${response.idLegalClient}`
    : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Box>
            <Typography variant='h6' component='div'>
              Bewertungsantwort
            </Typography>
            {response && (
              <Typography variant='caption' color='text.secondary'>
                {response.RatingFormTitle ?? '—'} · {recipient}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label='Schließen'>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoadingResponse || !response ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Übersicht
              </Typography>
              <Table size='small'>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 200 }} component='th'>
                      Status
                    </TableCell>
                    <TableCell>
                      {/* Klartext statt des englischen Rohwerts — dieselbe
                          Übersetzung wie in der Versandübersicht. */}
                      <Chip
                        size='small'
                        color={ratingStatusColor(response.Status)}
                        label={ratingStatusLabel(response.Status)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Versendet</TableCell>
                    <TableCell>
                      {formatBackendDateTime(response.SentAt)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Geöffnet</TableCell>
                    <TableCell>
                      {formatBackendDateTime(response.OpenedAt)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Abgegeben</TableCell>
                    <TableCell>
                      {formatBackendDateTime(response.SubmittedAt)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Gesamtbewertung</TableCell>
                    <TableCell>
                      {response.OverallScore != null ? (
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <StarsView
                            value={Math.round(Number(response.OverallScore))}
                            total={OVERALL_SCALE_MAX}
                          />
                          <Typography variant='body2'>
                            ({Number(response.OverallScore).toFixed(2)})
                          </Typography>
                        </Stack>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                  </TableRow>
                  {response.MailErrorMessage && (
                    <TableRow>
                      <TableCell component='th'>Mail-Fehler</TableCell>
                      <TableCell sx={{ color: 'error.main' }}>
                        {response.MailErrorMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Divider />

            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Antworten
              </Typography>
              {response.Answers.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Es liegen noch keine Einzelantworten vor.
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Frage</TableCell>
                      <TableCell sx={{ width: 200 }}>Antwort</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {response.Answers.map(a => (
                      <TableRow key={a.idRatingAnswer}>
                        <TableCell>
                          {a.QuestionLabel ??
                            `Frage #${a.idRatingFormQuestion}`}
                        </TableCell>
                        <TableCell>
                          <AnswerView answer={a} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box>
              <Typography variant='caption' color='text.secondary'>
                Token: {response.Token}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
